import React, { useState, useEffect, useRef } from 'react';
import { watchlistAPI } from '../lib/api';
import { formatDate, formatNumber, formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Search,
  RefreshCw,
  Shield,
  AlertTriangle,
  Globe,
  CheckCircle,
  XCircle,
  User,
  Building,
  FileText,
  History,
  Zap,
  ExternalLink,
  Upload,
  Download,
  Clock,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Watchlists() {
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [screeningResults, setScreeningResults] = useState(null);
  const [screening, setScreening] = useState(false);
  
  // Namescan state
  const [namescanStatus, setNamescanStatus] = useState(null);
  const [namescanScreenings, setNamescanScreenings] = useState([]);
  const [showNamescanModal, setShowNamescanModal] = useState(false);
  const [namescanForm, setNamescanForm] = useState({
    type: 'person',
    name: '',
    dateOfBirth: '',
    country: '',
  });
  const [namescanResult, setNamescanResult] = useState(null);
  const [namescanLoading, setNamescanLoading] = useState(false);
  const [selectedScreening, setSelectedScreening] = useState(null);
  
  // Batch screening state
  const [batchFile, setBatchFile] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchWatchlists();
    fetchNamescanStatus();
    fetchNamescanScreenings();
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await watchlistAPI.namescan.listBatches();
      setBatches(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const fetchWatchlists = async () => {
    setLoading(true);
    try {
      const response = await watchlistAPI.list();
      setWatchlists(response.data);
    } catch (error) {
      console.error('Failed to fetch watchlists:', error);
      toast.error('Failed to load watchlists');
    } finally {
      setLoading(false);
    }
  };

  const fetchNamescanStatus = async () => {
    try {
      const response = await watchlistAPI.namescan.status();
      setNamescanStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Namescan status:', error);
    }
  };

  const fetchNamescanScreenings = async () => {
    try {
      const response = await watchlistAPI.namescan.listScreenings({ limit: 20 });
      setNamescanScreenings(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch screenings:', error);
    }
  };

  const handleScreening = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setScreening(true);
    setScreeningResults(null);
    try {
      const response = await watchlistAPI.screen(searchQuery);
      setScreeningResults(response.data);
      if (response.data.hit_count > 0) {
        toast.warning(`Found ${response.data.hit_count} watchlist matches`);
      } else {
        toast.success('No watchlist matches found');
      }
    } catch (error) {
      toast.error('Screening failed');
    } finally {
      setScreening(false);
    }
  };

  const handleNamescanScreening = async (e) => {
    e.preventDefault();
    if (!namescanForm.name.trim()) {
      toast.error('Please enter a name to screen');
      return;
    }

    setNamescanLoading(true);
    setNamescanResult(null);
    try {
      let response;
      if (namescanForm.type === 'person') {
        response = await watchlistAPI.namescan.screenPerson(
          namescanForm.name,
          namescanForm.dateOfBirth || null,
          namescanForm.country || null
        );
      } else {
        response = await watchlistAPI.namescan.screenOrganisation(
          namescanForm.name,
          namescanForm.country || null
        );
      }
      
      setNamescanResult(response.data);
      fetchNamescanScreenings();
      
      const resultData = response.data.result;
      const hits = resultData?.numberOfMatches || resultData?.persons?.length || resultData?.organisations?.length || 0;
      
      if (hits > 0) {
        toast.warning(`Namescan found ${hits} potential matches`);
      } else {
        toast.success('No matches found in global watchlists');
      }
    } catch (error) {
      toast.error('Namescan screening failed');
      console.error('Namescan error:', error);
    } finally {
      setNamescanLoading(false);
    }
  };

  const viewScreeningDetails = async (screeningId) => {
    try {
      const response = await watchlistAPI.namescan.getScreening(screeningId);
      setSelectedScreening(response.data);
    } catch (error) {
      toast.error('Failed to load screening details');
    }
  };

  const getMatchSeverity = (score) => {
    if (score >= 90) return { color: 'text-red-400 bg-red-500/10 border-red-500/30', label: 'Critical' };
    if (score >= 70) return { color: 'text-orange-400 bg-orange-500/10 border-orange-500/30', label: 'High' };
    if (score >= 50) return { color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30', label: 'Medium' };
    return { color: 'text-green-400 bg-green-500/10 border-green-500/30', label: 'Low' };
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="watchlists-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Entity Screening
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Screen entities against global sanctions, PEP, and adverse media lists
          </p>
        </div>
        <div className="flex items-center gap-3">
          {namescanStatus?.configured && (
            <Badge variant="outline" className="text-green-400 border-green-400/30">
              <Zap className="w-3 h-3 mr-1" />
              Data Sources Connected
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => { fetchWatchlists(); fetchNamescanScreenings(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="screening" className="space-y-4">
        <TabsList className="glass">
          <TabsTrigger value="screening" className="gap-2">
            <Search className="w-4 h-4" />
            Screening
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="batch" className="gap-2" data-testid="batch-tab">
            <Upload className="w-4 h-4" />
            Batch
          </TabsTrigger>
          <TabsTrigger value="watchlists" className="gap-2">
            <Shield className="w-4 h-4" />
            Watchlists
          </TabsTrigger>
        </TabsList>

        {/* Entity Screening Tab - Uses integrated screening service */}
        <TabsContent value="screening" className="space-y-4">
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                Quick Entity Screening
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Screen against sanctions, PEP, and adverse media lists
              </p>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleNamescanScreening} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-mono">Entity Type</Label>
                    <Select
                      value={namescanForm.type}
                      onValueChange={(value) => setNamescanForm({ ...namescanForm, type: value })}
                    >
                      <SelectTrigger className="bg-black/20 border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="person">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Person / Individual
                          </div>
                        </SelectItem>
                        <SelectItem value="organisation">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4" />
                            Organisation / Company
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">Country (Optional)</Label>
                    <Input
                      value={namescanForm.country}
                      onChange={(e) => setNamescanForm({ ...namescanForm, country: e.target.value })}
                      placeholder="e.g., US, GB, AU"
                      className="bg-black/20 border-white/10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="label-mono">Name to Screen *</Label>
                  <Input
                    value={namescanForm.name}
                    onChange={(e) => setNamescanForm({ ...namescanForm, name: e.target.value })}
                    placeholder="Enter entity name to screen..."
                    className="bg-black/20 border-white/10"
                    required
                  />
                </div>

                {namescanForm.type === 'person' && (
                  <div className="space-y-2">
                    <Label className="label-mono">Date of Birth (Optional)</Label>
                    <Input
                      type="date"
                      value={namescanForm.dateOfBirth}
                      onChange={(e) => setNamescanForm({ ...namescanForm, dateOfBirth: e.target.value })}
                      className="bg-black/20 border-white/10"
                    />
                  </div>
                )}

                <Button type="submit" disabled={namescanLoading || !namescanForm.name.trim()} className="w-full">
                  {namescanLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Screen Entity
                </Button>
              </form>

              {/* Screening Results */}
              {namescanResult && (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-white">Screening Results</h3>
                    <span className="text-xs text-muted-foreground">
                      Ref: {namescanResult.screening_id?.slice(0, 8)}...
                    </span>
                  </div>

                  {/* Results Summary */}
                  <div className={`p-4 rounded-lg border ${
                    (namescanResult.result?.numberOfMatches > 0 || namescanResult.result?.persons?.length > 0 || namescanResult.result?.organisations?.length > 0)
                      ? 'bg-red-500/10 border-red-500/30'
                      : 'bg-green-500/10 border-green-500/30'
                  }`}>
                    <div className="flex items-center gap-3">
                      {(namescanResult.result?.numberOfMatches > 0 || namescanResult.result?.persons?.length > 0) ? (
                        <AlertTriangle className="w-6 h-6 text-red-400" />
                      ) : (
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      )}
                      <div>
                        <p className={`font-medium ${
                          (namescanResult.result?.numberOfMatches > 0 || namescanResult.result?.persons?.length > 0)
                            ? 'text-red-400'
                            : 'text-green-400'
                        }`}>
                          {namescanResult.result?.numberOfMatches || namescanResult.result?.persons?.length || namescanResult.result?.organisations?.length || 0} Potential Matches
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Screened: {namescanResult.query?.name}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Match Details */}
                  {(namescanResult.result?.persons || namescanResult.result?.organisations || namescanResult.result?.scanResult?.matchedEntities)?.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-xs font-mono text-muted-foreground uppercase">Match Details</h4>
                      {(namescanResult.result?.persons || namescanResult.result?.organisations || namescanResult.result?.scanResult?.matchedEntities || []).map((match, index) => (
                        <Card key={index} className="bg-white/5 border-white/10">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <p className="font-medium text-white">{match.name || match.fullName}</p>
                                <p className="text-xs text-muted-foreground">
                                  {match.category || match.matchTypes?.join(', ') || 'Watchlist Match'}
                                </p>
                              </div>
                              {match.matchScore && (
                                <Badge variant="outline" className={getMatchSeverity(match.matchScore).color}>
                                  {match.matchScore}% Match
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {match.countries && (
                                <div>
                                  <span className="text-muted-foreground">Countries: </span>
                                  <span className="text-white">{match.countries?.join(', ')}</span>
                                </div>
                              )}
                              {match.dateOfBirth && (
                                <div>
                                  <span className="text-muted-foreground">DOB: </span>
                                  <span className="text-white">{match.dateOfBirth}</span>
                                </div>
                              )}
                              {match.listName && (
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">List: </span>
                                  <span className="text-white">{match.listName}</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Screening History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                Screening History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {namescanScreenings.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Screened</TableHead>
                      <TableHead>Results</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {namescanScreenings.map((screening) => {
                      const hits = screening.result?.numberOfMatches || screening.result?.persons?.length || screening.result?.organisations?.length || 0;
                      return (
                        <TableRow key={screening.id} className="border-white/5">
                          <TableCell className="font-medium text-white">
                            {screening.query_name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {screening.type === 'person' ? <User className="w-3 h-3 mr-1" /> : <Building className="w-3 h-3 mr-1" />}
                              {screening.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {screening.query_country || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {formatRelativeTime(screening.screened_at)}
                          </TableCell>
                          <TableCell>
                            {hits > 0 ? (
                              <Badge variant="outline" className="text-red-400 border-red-400/30">
                                {hits} matches
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-400 border-green-400/30">
                                Clear
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewScreeningDetails(screening.id)}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <History className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No screening history</p>
                  <p className="text-sm text-muted-foreground/50 mt-1">
                    Screening results will appear here
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Batch Screening Tab */}
        <TabsContent value="batch" className="space-y-4" data-testid="batch-content">
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-secondary text-sm uppercase tracking-wider flex items-center gap-2">
                    <Upload className="w-4 h-4 text-primary" />
                    Batch Screening
                  </CardTitle>
                  <CardDescription className="mt-2">
                    Upload a CSV file to screen multiple entities at once
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchBatches} className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* CSV Upload Section */}
              <div className="p-6 border-2 border-dashed border-gray-700 rounded-lg text-center bg-gray-900/30">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => setBatchFile(e.target.files[0])}
                />
                <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                <p className="text-white font-medium mb-1">Upload CSV File</p>
                <p className="text-gray-400 text-sm mb-4">
                  CSV with columns: name, type (person/organisation), country, date_of_birth
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Select File
                  </Button>
                  {batchFile && (
                    <Badge variant="outline" className="text-cyan-400 border-cyan-400/30">
                      {batchFile.name}
                    </Badge>
                  )}
                </div>
                {batchFile && (
                  <Button
                    className="mt-4 gap-2"
                    onClick={async () => {
                      setBatchLoading(true);
                      try {
                        const response = await watchlistAPI.namescan.batchScreenCSV(batchFile);
                        toast.success(`Batch screening started! ID: ${response.data.batch_id}`);
                        setBatchFile(null);
                        fileInputRef.current.value = '';
                        fetchBatches();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to start batch screening');
                      } finally {
                        setBatchLoading(false);
                      }
                    }}
                    disabled={batchLoading}
                  >
                    {batchLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Zap className="w-4 h-4" />
                    )}
                    Start Batch Screening
                  </Button>
                )}
              </div>

              {/* CSV Template Download */}
              <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">Need a template?</p>
                  <p className="text-gray-400 text-xs">Download a sample CSV file with the correct format</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    const csv = 'name,type,country,date_of_birth\nJohn Doe,person,US,1980-01-15\nAcme Corp,organisation,UK,\nJane Smith,person,DE,1975-06-20';
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'screening_template.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Batch Jobs */}
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider flex items-center gap-2">
                <History className="w-4 h-4 text-primary" />
                Batch Screening History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {batches.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch ID</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Failed</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.map((batch) => (
                      <TableRow key={batch.id} className="border-white/5">
                        <TableCell className="font-mono text-xs">{batch.id.slice(0, 8)}...</TableCell>
                        <TableCell>{batch.total}</TableCell>
                        <TableCell className="text-green-400">{batch.completed}</TableCell>
                        <TableCell className="text-red-400">{batch.failed}</TableCell>
                        <TableCell>
                          {batch.status === 'completed' ? (
                            <Badge variant="outline" className="text-green-400 border-green-400/30 gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Completed
                            </Badge>
                          ) : batch.status === 'in_progress' ? (
                            <Badge variant="outline" className="text-cyan-400 border-cyan-400/30 gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              In Progress
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">
                              {batch.status}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {formatRelativeTime(batch.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              const response = await watchlistAPI.namescan.getBatch(batch.id);
                              setSelectedBatch(response.data);
                            }}
                          >
                            View Results
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Upload className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No batch screenings yet</p>
                  <p className="text-muted-foreground text-sm mt-1">Upload a CSV file to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Watchlists Tab */}
        <TabsContent value="watchlists" className="space-y-4">
          <Card className="glass">
            <CardHeader className="border-b border-white/5 pb-4">
              <CardTitle className="font-secondary text-sm uppercase tracking-wider">
                Active Watchlists
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading watchlists...</p>
                </div>
              ) : watchlists.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Watchlist</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Entries</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {watchlists.map((list) => (
                      <TableRow key={list.id} className="border-white/5">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded bg-white/5">
                              <Shield className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-white">{list.name}</p>
                              <p className="text-xs text-muted-foreground">{list.source}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{list.list_type}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-primary">
                          {formatNumber(list.entry_count || 0)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {list.last_updated ? formatDate(list.last_updated) : '-'}
                        </TableCell>
                        <TableCell>
                          {list.is_active ? (
                            <Badge variant="outline" className="text-green-400 border-green-400/30">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-12 text-center">
                  <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No watchlists configured</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Screening Details Modal */}
      <Dialog open={!!selectedScreening} onOpenChange={() => setSelectedScreening(null)}>
        <DialogContent className="glass border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
              Screening Details
            </DialogTitle>
          </DialogHeader>
          {selectedScreening && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/20 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Name Screened</p>
                  <p className="text-white font-medium">{selectedScreening.query_name}</p>
                </div>
                <div className="bg-black/20 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="text-white font-medium capitalize">{selectedScreening.type}</p>
                </div>
                <div className="bg-black/20 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Screening ID</p>
                  <p className="text-white font-mono text-xs">{selectedScreening.id}</p>
                </div>
                <div className="bg-black/20 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Screened At</p>
                  <p className="text-white text-sm">{formatDate(selectedScreening.screened_at)}</p>
                </div>
              </div>
              
              <div className="bg-black/20 p-4 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Raw Results</p>
                <pre className="text-xs text-muted-foreground overflow-x-auto">
                  {JSON.stringify(selectedScreening.result, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Results Modal */}
      <Dialog open={!!selectedBatch} onOpenChange={() => setSelectedBatch(null)}>
        <DialogContent className="glass border-border max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Batch Screening Results
            </DialogTitle>
          </DialogHeader>
          {selectedBatch && (
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-cyan-500/10 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-cyan-400">{selectedBatch.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="bg-green-500/10 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{selectedBatch.completed}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
                <div className="bg-red-500/10 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-400">{selectedBatch.failed}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
                <div className="bg-orange-500/10 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-orange-400">
                    {selectedBatch.results?.filter(r => r.has_matches).length || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">With Matches</p>
                </div>
              </div>

              {/* Progress bar */}
              {selectedBatch.status === 'in_progress' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Progress</span>
                    <span>{Math.round((selectedBatch.completed / selectedBatch.total) * 100)}%</span>
                  </div>
                  <Progress value={(selectedBatch.completed / selectedBatch.total) * 100} className="h-2" />
                </div>
              )}

              {/* Results Table */}
              {selectedBatch.results && selectedBatch.results.length > 0 && (
                <div className="border border-gray-700 rounded-lg overflow-hidden">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Matches</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedBatch.results.map((result, idx) => (
                        <TableRow key={idx} className="border-white/5">
                          <TableCell className="font-medium text-white">{result.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs capitalize">
                              {result.type === 'person' ? <User className="w-3 h-3 mr-1 inline" /> : <Building className="w-3 h-3 mr-1 inline" />}
                              {result.type}
                            </Badge>
                          </TableCell>
                          <TableCell>{result.country || '-'}</TableCell>
                          <TableCell>
                            {result.status === 'completed' ? (
                              <Badge variant="outline" className="text-green-400 border-green-400/30 gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Done
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-red-400 border-red-400/30 gap-1">
                                <XCircle className="w-3 h-3" />
                                {result.error?.slice(0, 30) || 'Failed'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {result.has_matches ? (
                              <Badge className="bg-orange-500/20 text-orange-400 gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {result.match_count} matches
                              </Badge>
                            ) : result.status === 'completed' ? (
                              <Badge variant="outline" className="text-green-400 border-green-400/30">
                                Clear
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
